/* eslint-disable jsx-a11y/interactive-supports-focus */
import React, { lazy, Suspense, useState } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import Metrics from '@spotahome/soyuz-tracking';

import { useCookie } from '../utils/withCookie';
import { useModalHandle } from '../utils/withModalHandle';

import Modal from '../Modal';

import { ExternalLinkProvider } from '../../context/ExternalLink';

import SpottyCatHeader from './SpottyCatHeader';
import SpottyCatProgressBar from './SpottyCatProgressBar';

import {
  VALUE_PROPOSITION,
  INTRODUCTION,
  PROPERTY_TYPE,
  MOVE_IN,
  RENTAL_TYPE,
  CITY,
  BUDGET,
  EMAIL,
  SUCCESS,
  TOTAL_STEPS,
  FORM_URL_OPTIONS
} from './constants';

import {
  createSearchURLFromOptions,
  getFeaturedCitiesFormatted
} from './utils';

import styles from './SpottyCat.module.scss';

import SpottyCatIntro from './SpottyCatIntro';
import PropertyType from './screens/PropertyType';
import MoveIn from './screens/MoveIn';
import RentalType from './screens/RentalType';
import Budget from './screens/Budget';
import Email from './screens/Email';
import City from './screens/City';
import Success from './screens/Success';

const SpottyValueProposition = lazy(() => import('./SpottyValueProposition'));

const cx = classNames.bind(styles);

const TWO_WEEKS = 60 * 60 * 24 * 14; // 2 weeks in seconds

const SPOTTY_CAT_IMG =
  'https://d26q4asbryw2nm.cloudfront.net/static/spotty-rebranding.png';

const SpottyCat = ({
  locale,
  user,
  cities,
  isUserLogged,
  isWhatsappShown,
  isContactUsShown,
  onSuccess,
  onSignupWithoutPassword
}) => {
  const [step, setStep] = useState(VALUE_PROPOSITION);
  const { getCookie, setCookie } = useCookie();
  const { isModalOpen, handleModalOpen, handleModalClose } = useModalHandle();
  const [propertyType, setPropertyType] = useState(null);
  const [moveIn, setMoveIn] = useState(null);
  const [rentalType, setRentalType] = useState(null);
  const [budget, setBudget] = useState(null);
  const [email, setEmail] = useState((user && user.email) || '');
  const [city, setCity] = useState('');

  const hasUsedSpottyCat = getCookie('SpottyCatUsed') !== undefined;
  const cityId = city && cities.find(c => c.cityName === city).id;

  const searchUrl = createSearchURLFromOptions(
    locale,
    [
      FORM_URL_OPTIONS.propertyType[propertyType],
      FORM_URL_OPTIONS.moveIn[moveIn],
      FORM_URL_OPTIONS.rentalType[rentalType],
      FORM_URL_OPTIONS.budget[budget]
    ],
    cityId
  );

  const resetForm = () => {
    setPropertyType('');
    setMoveIn('');
    setRentalType('');
    setCity('');
    setEmail('');
    setBudget('');
  };

  const handleSpottyCatClose = () => {
    Metrics.ga.sendEvent('SpottyCat', 'Close', 'click');
    handleModalClose();
    setCookie('SpottyCatUsed', 1, { maxAge: TWO_WEEKS });
    setStep(VALUE_PROPOSITION);
    resetForm();
  };

  const handleNextStep = () => {
    setTimeout(() => {
      const nextStep = step + 1;
      Metrics.ga.sendEvent('SpottyCat', 'NextStep', nextStep);
      if (nextStep === EMAIL && user && user.email) {
        setStep(nextStep + 1);
      } else {
        setStep(nextStep);
      }
    }, 500);
  };

  const handlePrevStep = () => {
    Metrics.ga.sendEvent('SpottyCat', 'Back', 'click');
    setStep(step - 1);
  };

  const handleValuePropOpen = () => {
    handleModalOpen();
    Metrics.ga.sendEvent('SpottyCat', 'OpenCat', 'open-click');
  };

  const handleValuePropClose = () => {
    handleModalClose();
    Metrics.ga.sendEvent('SpottyCat', 'ValuePropClose', 'click');
    setCookie('SpottyCatUsed', 1, { maxAge: TWO_WEEKS });
    resetForm();
  };

  const handleValuePropNext = () => {
    handleModalClose();
    setStep(1);
    Metrics.ga.sendEvent('SpottyCat', 'OpenForm', 'open-click');
  };

  const handleOptionSelected = optionValue => {
    switch (step) {
      case PROPERTY_TYPE:
        Metrics.ga.sendEvent('SpottyCat', 'FormPropertyType', optionValue);
        setPropertyType(optionValue);
        break;
      case MOVE_IN:
        Metrics.ga.sendEvent('SpottyCat', 'FormMoveIn', optionValue);
        setMoveIn(optionValue);
        break;
      case RENTAL_TYPE:
        Metrics.ga.sendEvent('SpottyCat', 'FormRentalType', optionValue);
        setRentalType(optionValue);
        break;
      case CITY:
        Metrics.ga.sendEvent(
          'SpottyCat',
          'FormCity',
          cities.find(c => c.cityName === optionValue).id
        );
        setCity(optionValue);
        break;
      case BUDGET:
        Metrics.ga.sendEvent('SpottyCat', 'FormBudget', optionValue);
        setBudget(optionValue);
        break;
      case EMAIL:
        setEmail(optionValue);
        break;
      default:
    }
    handleNextStep();
  };

  const spottyCatImageClasses = cx(styles['spotty-cat__image'], {
    [styles['spotty-cat__image--hidden']]: hasUsedSpottyCat
  });

  const notificationClasses = cx(styles['spotty-cat__notification'], {
    [styles['spotty-cat__notification--hidden']]: hasUsedSpottyCat
  });

  return (
    <>
      <div onClick={handleValuePropOpen} role="button">
        <img
          src={SPOTTY_CAT_IMG}
          alt="spotty cat"
          className={spottyCatImageClasses}
        />
        <span className={notificationClasses}>1</span>
      </div>

      <Suspense fallback={null}>
        <SpottyValueProposition
          isOpen={isModalOpen}
          onClose={handleValuePropClose}
          onNextStep={handleValuePropNext}
        />
      </Suspense>

      <ExternalLinkProvider currentLocale={locale}>
        <Modal
          className={styles['spotty-cat__modal']}
          contentLabel="SpottyCatModal"
          isOpen={step > VALUE_PROPOSITION}
          onRequestClose={handleSpottyCatClose}
          size="big"
        >
          {step >= PROPERTY_TYPE && (
            <SpottyCatHeader
              showBackButton={step >= MOVE_IN && step < SUCCESS}
              handleBackButton={handlePrevStep}
              handleCloseButton={handleSpottyCatClose}
            />
          )}

          <div className={styles['spotty-cat__screen']}>
            {step === INTRODUCTION && (
              <SpottyCatIntro onNextStep={handleNextStep} />
            )}
            {step === PROPERTY_TYPE && (
              <PropertyType
                propertyType={propertyType}
                onPropertyTypeSelected={handleOptionSelected}
              />
            )}
            {step === MOVE_IN && (
              <MoveIn onMoveInSelected={handleOptionSelected} moveIn={moveIn} />
            )}
            {step === RENTAL_TYPE && (
              <RentalType
                onRentalTypeSelected={handleOptionSelected}
                rentalType={rentalType}
              />
            )}
            {step === CITY && (
              <City
                city={city}
                cityList={cities}
                onCitySelected={handleOptionSelected}
                featuredCities={getFeaturedCitiesFormatted(cities)}
              />
            )}
            {step === BUDGET && (
              <Budget onBudgetSelected={handleOptionSelected} budget={budget} />
            )}
            {step === EMAIL && (
              <Email
                name={user && user.fullname}
                email={email}
                locale={locale}
                cityId={cityId}
                searchUrl={searchUrl}
                isUserLogged={isUserLogged}
                onSuccess={onSuccess}
                onEmailSelected={handleOptionSelected}
                onSignupWithoutPassword={onSignupWithoutPassword}
              />
            )}
            {step === SUCCESS && (
              <Success
                email={email}
                searchUrl={searchUrl}
                isWhatsappShown={isWhatsappShown}
                isContactUsShown={isContactUsShown}
              />
            )}
          </div>

          {step > INTRODUCTION && (
            <SpottyCatProgressBar step={step} totalSteps={TOTAL_STEPS} />
          )}
        </Modal>
      </ExternalLinkProvider>
    </>
  );
};

SpottyCat.propTypes = {
  user: PropTypes.shape({
    email: PropTypes.string,
    fullname: PropTypes.string
  }),
  cities: PropTypes.arrayOf(PropTypes.string).isRequired,
  locale: PropTypes.string,
  onSuccess: PropTypes.func.isRequired,
  onSignupWithoutPassword: PropTypes.func,
  isUserLogged: PropTypes.bool,
  isWhatsappShown: PropTypes.bool,
  isContactUsShown: PropTypes.bool
};

SpottyCat.defaultProps = {
  user: {
    email: '',
    fullname: ''
  },
  locale: 'en',
  onSignupWithoutPassword: null,
  isUserLogged: false,
  isWhatsappShown: false,
  isContactUsShown: false
};

export default SpottyCat;
